/* ==========================================================================================
   CE 2026 · ce-collection.js · the collection page, in place (collections builder, launch wave 10-10-2026;
   forked from the earlier theme's collection script)
   sections/ce-main-collection.liquid ([data-ce-coll]), ce-collection-hero.liquid ([data-ce-ch]) and
   ce-collection-recent.liquid ([data-ce-crecent]). No dependencies; deferred; idempotent; re-binds on
   shopify:section:load.

   [data-ce-coll]                  one products section. Inside it:
     form[data-ce-facets-form]     the filter form (snippets/ce-facets). Ticking, typing a price or moving a slider
                                   asks Shopify's Section Rendering API for this section (?section_id=...), swaps
                                   every [data-ce-swap="key"] part, and keeps a real address in the bar (pushState;
                                   Back and Forward work). A submit of the form (the size sliders send one, see
                                   assets/ce-dims-range.js) is applied the same way.
     select[data-ce-sort]          the sort (joined to the form by its form="" attribute)
     select[data-ce-sort-proxy]    the drawer's copy of the sort (phones)
     a[data-ce-coll-link]          chips, Clear all, pages: applied in place ([data-ce-top] scrolls to the grid)
     [data-ce-coll-open] / [data-ce-coll-close] / [data-ce-coll-drawer]   the filter panel: a SIDEBAR from 1200px
                                   (always there), a drawer below that
     [data-ce-coll-stick]          the sticky "Filter and sort" button on phones (while the grid is on screen and the
                                   bar's own button is not)
     [data-ce-price]               the price slider: two range inputs mirrored into the two number boxes
   [data-ce-ch-desc]               the header's description: 3 lines, Read more / Show less (the button only shows
                                   when there is more to read)
   [data-ce-crecent]               "Recently viewed": the product handles this browser saw (localStorage
                                   'ce:recent', written by the product page), each card asked from
                                   /products/<handle>?section_id=ce-product-recs
   Any failure falls back to a normal page load of the same address, so filtering never breaks.
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CEColl) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const drawerMq = window.matchMedia('(max-width: 1199px)');
  const stickMq = window.matchMedia('(max-width: 749px)');
  const KEYS = ['facets', 'clear', 'badge', 'count', 'active', 'done', 'results'];

  /* ---------- the header's description: Read more ---------- */
  const descs = (root) => {
    root.querySelectorAll('[data-ce-ch-desc]').forEach((d) => {
      const btn = d.querySelector('[data-ce-ch-more]');
      const text = d.querySelector('.ce-ch__text');
      if (!btn || !text) return;
      if (!d.ceBound) {
        d.ceBound = true;
        btn.addEventListener('click', () => {
          const open = !d.classList.contains('is-open');
          d.classList.toggle('is-open', open);
          btn.setAttribute('aria-expanded', String(open));
          if (!open) {
            const top = d.getBoundingClientRect().top;
            if (top < 80) window.scrollBy({ top: top - 120, behavior: reduce.matches ? 'auto' : 'smooth' });
          }
        });
      }
      if (d.classList.contains('is-open')) return;
      const long = text.scrollHeight > text.clientHeight + 4;
      btn.hidden = !long;
      d.classList.toggle('is-short', !long);
    });
  };

  /* ---------- the price slider ---------- */
  const priceBind = (root) => {
    root.querySelectorAll('[data-ce-price]').forEach((w) => {
      if (w.ceBound) return;
      w.ceBound = true;
      const max = parseFloat(w.dataset.max) || 1;
      const rMin = w.querySelector('[data-ce-range="min"]');
      const rMax = w.querySelector('[data-ce-range="max"]');
      const fMin = w.querySelector('[data-ce-field="min"]');
      const fMax = w.querySelector('[data-ce-field="max"]');
      if (!rMin || !rMax || !fMin || !fMax) return;
      const paint = () => {
        const lo = Math.min(+rMin.value, +rMax.value);
        const hi = Math.max(+rMin.value, +rMax.value);
        w.style.setProperty('--lo', (lo / max).toFixed(4));
        w.style.setProperty('--hi', (1 - hi / max).toFixed(4));
        rMin.style.zIndex = lo / max > 0.5 ? '4' : '3';
      };
      const fromRange = (e) => {
        let a = +rMin.value, b = +rMax.value;
        if (a > b) { if (e.target === rMin) rMin.value = a = b; else rMax.value = b = a; }
        fMin.value = a > 0 ? a : '';
        fMax.value = b < max ? b : '';
        paint();
      };
      const fromField = () => {
        const a = fMin.value === '' ? 0 : Math.max(0, Math.min(max, +fMin.value || 0));
        const b = fMax.value === '' ? max : Math.max(0, Math.min(max, +fMax.value || max));
        rMin.value = Math.min(a, b);
        rMax.value = Math.max(a, b);
        paint();
      };
      rMin.addEventListener('input', fromRange);
      rMax.addEventListener('input', fromRange);
      fMin.addEventListener('input', fromField);
      fMax.addEventListener('input', fromField);
      paint();
    });
  };

  const dimsBind = (root) => { if (window.CEDims && window.CEDims.bind) window.CEDims.bind(root); };

  class Coll {
    constructor(el) {
      this.el = el;
      this.id = el.dataset.section;
      this.cache = new Map();
      this.timer = 0;
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      el.addEventListener('change', this.onChange);
      el.addEventListener('input', this.onInput);
      el.addEventListener('click', this.onClick);
      el.addEventListener('submit', (e) => {
        if (e.target.matches('[data-ce-facets-form]')) { e.preventDefault(); this.apply(); }
      });
      this.aside = el.querySelector('[data-ce-coll-drawer]');
      this.scrim = el.querySelector('.ce-coll__scrim');
      this.stickInit();
      priceBind(el);
      dimsBind(el);
    }

    get form() { return this.el.querySelector('[data-ce-facets-form]'); }

    onChange(e) {
      const t = e.target;
      if (t.closest && t.closest('[data-ce-dim]')) return;           // the size sliders apply themselves
      if (t.matches('[data-ce-sort-proxy]')) {
        const real = this.el.querySelector('[data-ce-sort]');
        if (real) real.value = t.value;
        return this.apply();
      }
      if (t.matches('[data-ce-sort]')) return this.apply();
      if (t.matches('[data-ce-range]')) return this.apply(150);
      if (t.form && t.form.matches('[data-ce-facets-form]')) {
        if (t.type === 'number') return this.apply(300);
        return this.apply();
      }
      return undefined;
    }

    onInput(e) {
      const t = e.target;
      if (t.closest && t.closest('[data-ce-dim]')) return;
      if (t.type === 'number' && t.form && t.form.matches('[data-ce-facets-form]')) this.apply(900);
    }

    onClick(e) {
      const a = e.target.closest('a[data-ce-coll-link]');
      if (a && this.el.contains(a) && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
        e.preventDefault();
        this.render(a.href, { top: a.hasAttribute('data-ce-top') });
        return;
      }
      const opener = e.target.closest('[data-ce-coll-open]');
      if (opener) { this.open(opener); return; }
      if (e.target.closest('[data-ce-coll-close]')) this.close();
    }

    onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const panel = this.aside && this.aside.querySelector('.ce-coll__panel');
      if (!panel) return;
      const f = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), select, summary, [tabindex]:not([tabindex="-1"])')]
        .filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* the address from the form: filters + sort; an empty box and a whole-range price are left out */
    urlFromForm() {
      const params = new URLSearchParams();
      const form = this.form;
      if (form) {
        for (const [k, v] of new FormData(form).entries()) {
          const val = String(v).trim();
          if (val === '') continue;
          if (k.includes('price')) { const n = val.replace(/[^\d.]/g, ''); if (n !== '') params.append(k, n); continue; }
          params.append(k, val);
        }
      }
      const w = this.el.querySelector('[data-ce-price]');
      if (w) {
        const max = parseFloat(w.dataset.max);
        const fMin = w.querySelector('[data-ce-field="min"]');
        const fMax = w.querySelector('[data-ce-field="max"]');
        if (fMin && fMin.value !== '' && +fMin.value <= 0) params.delete(fMin.name);
        if (fMax && fMax.value !== '' && +fMax.value >= max) params.delete(fMax.name);
      }
      const sort = this.el.querySelector('[data-ce-sort]');
      if (sort && sort.dataset.default && params.get('sort_by') === sort.dataset.default) params.delete('sort_by');
      const qs = params.toString();
      return window.location.pathname + (qs ? '?' + qs : '');
    }

    apply(delay = 0) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.render(this.urlFromForm()), delay);
    }

    sectionUrl(href) {
      const u = new URL(href, window.location.origin);
      u.searchParams.delete('section_id');
      u.searchParams.delete('page_view');
      const clean = u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '') + u.hash;
      u.searchParams.set('section_id', this.id);
      return { clean, fetchUrl: u.pathname + '?' + u.searchParams.toString() };
    }

    async fetchHTML(url) {
      if (this.cache.has(url)) return this.cache.get(url);
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const res = await fetch(url, { signal: this.ctrl.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      if (this.cache.size > 24) this.cache.clear();
      this.cache.set(url, html);
      return html;
    }

    setLoading(on) {
      this.el.classList.toggle('is-loading', on);
      const r = this.el.querySelector('[data-ce-swap="results"]');
      if (r) r.setAttribute('aria-busy', String(on));
    }

    async render(href, opts = {}) {
      const { clean, fetchUrl } = this.sectionUrl(href);
      this.setLoading(true);
      let html;
      try {
        html = await this.fetchHTML(fetchUrl);
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.href = clean;
        return;
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (!doc.querySelector('[data-ce-swap="results"]')) { window.location.href = clean; return; }
      this.swap(doc);
      this.setLoading(false);
      if (opts.push !== false) history.pushState({ ceColl: this.id }, '', clean);
      if (this.isOpen()) { this.dirty = true; return; }
      if (opts.top || opts.push !== false) this.toResults(!!opts.top);
    }

    swap(doc) {
      const el = this.el;
      const open = {};
      el.querySelectorAll('details[id]').forEach((d) => { open[d.id] = d.open; });
      const active = document.activeElement;
      let focusSel = '';
      if (active && el.contains(active)) {
        if (active.id) focusSel = '#' + CSS.escape(active.id);
        else if (active.dataset.ceField) focusSel = '[data-ce-field="' + active.dataset.ceField + '"]';
        else if (active.dataset.ceRange) focusSel = '[data-ce-range="' + active.dataset.ceRange + '"]';
        else if (active.closest('.ce-coll__active')) focusSel = '.ce-coll__active a, [data-ce-sort]';
      }
      const body = el.querySelector('.ce-coll__panel-body');
      const top = body ? body.scrollTop : 0;
      const results = el.querySelector('[data-ce-swap="results"]');
      if (results && window.BO && BO.release) BO.release(results);

      KEYS.forEach((key) => {
        const now = el.querySelectorAll(`[data-ce-swap="${key}"]`);
        const next = doc.querySelectorAll(`[data-ce-swap="${key}"]`);
        now.forEach((n, i) => { if (next[i]) n.innerHTML = next[i].innerHTML; });
      });
      const sortNext = doc.querySelector('[data-ce-sort]');
      el.querySelectorAll('[data-ce-sort], [data-ce-sort-proxy]').forEach((s) => { if (sortNext) s.value = sortNext.value; });

      el.querySelectorAll('details[id]').forEach((d) => { if (d.id in open) d.open = open[d.id]; });
      if (body) body.scrollTop = top;
      if (focusSel) {
        const f = el.querySelector(focusSel);
        if (f) f.focus({ preventScroll: true });
      }
      priceBind(el);
      dimsBind(el);
      if (window.BO && BO.init) BO.init(el.querySelector('[data-ce-swap="results"]') || el);
      if (window.CEFx && CEFx.init) CEFx.init(el);
      this.stickCheck();
    }

    toResults(force) {
      const target = this.el.querySelector('.ce-coll__main');
      if (!target) return;
      const offset = (parseFloat(getComputedStyle(this.el).getPropertyValue('--ce-coll-top')) || 110);
      const y = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
      if (force || window.scrollY > y + 4) window.scrollTo({ top: y, behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    isOpen() { return !!(this.aside && this.aside.classList.contains('is-open')); }

    open(opener) {
      if (!this.aside || !drawerMq.matches) return;
      this.last = opener || null;
      this.aside.classList.add('is-open');
      if (this.scrim) this.scrim.classList.add('is-on');
      document.documentElement.classList.add('ce-coll-locked');
      this.el.querySelectorAll('[data-ce-coll-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const panel = this.aside.querySelector('.ce-coll__panel');
      if (panel) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        const close = panel.querySelector('.ce-coll__close');
        setTimeout(() => (close || panel).focus({ preventScroll: true }), 60);
      }
      document.addEventListener('keydown', this.onKey);
    }

    close() {
      if (!this.isOpen()) return;
      this.aside.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-on');
      document.documentElement.classList.remove('ce-coll-locked');
      this.el.querySelectorAll('[data-ce-coll-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const panel = this.aside.querySelector('.ce-coll__panel');
      if (panel) { panel.setAttribute('role', 'region'); panel.removeAttribute('aria-modal'); }
      document.removeEventListener('keydown', this.onKey);
      if (this.last && this.last.isConnected && this.last.offsetParent !== null) this.last.focus({ preventScroll: true });
      if (this.dirty) { this.dirty = false; requestAnimationFrame(() => this.toResults(false)); }
    }

    /* the sticky phone button: while the grid is on screen and the bar's own Filter button is not */
    stickInit() {
      this.stick = this.el.querySelector('[data-ce-coll-stick]');
      if (!this.stick || !('IntersectionObserver' in window)) return;
      this.seenMain = false;
      this.seenBar = true;
      const main = this.el.querySelector('.ce-coll__main');
      const bar = this.el.querySelector('.ce-coll__bar');
      this.io = new IntersectionObserver((ens) => {
        ens.forEach((en) => {
          if (en.target === main) this.seenMain = en.isIntersecting;
          if (en.target === bar) this.seenBar = en.isIntersecting;
        });
        this.stickCheck();
      }, { rootMargin: '0px 0px -120px 0px' });
      if (main) this.io.observe(main);
      if (bar) this.io.observe(bar);
    }

    stickCheck() {
      if (!this.stick) return;
      const grid = this.el.querySelector('[data-ce-coll-grid]');
      this.stick.classList.toggle('is-shown', !!grid && this.seenMain && !this.seenBar && stickMq.matches);
    }

    destroy() {
      if (this.io) this.io.disconnect();
      this.close();
    }
  }

  /* ---------- Recently viewed (collection pages) ---------- */
  const RKEY = 'ce:recent';
  const recentList = () => {
    try {
      const v = JSON.parse(localStorage.getItem(RKEY) || '[]');
      return Array.isArray(v) ? v.filter((h) => typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(h)) : [];
    } catch (e) { return []; }
  };
  const recent = (root) => {
    root.querySelectorAll('[data-ce-crecent]').forEach((sec) => {
      if (sec.ceBound) return;
      sec.ceBound = true;
      const limit = parseInt(sec.dataset.limit, 10) || 8;
      const want = recentList().slice(0, limit);
      if (!want.length) return;
      const base = (sec.dataset.root || '/').replace(/\/?$/, '/');
      const run = () => {
        Promise.all(want.map((h) => fetch(base + 'products/' + encodeURIComponent(h) + '?section_id=ce-product-recs', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.text() : ''))
          .then((t) => { const c = t && new DOMParser().parseFromString(t, 'text/html').querySelector('[data-ce-recs-card]'); return c ? c.firstElementChild : null; })
          .catch(() => null)))
          .then((cards) => {
            const track = sec.querySelector('[data-ce-crecent-track]');
            if (!track) return;
            track.textContent = '';
            cards.filter(Boolean).forEach((c) => { const li = document.createElement('li'); li.appendChild(c); track.appendChild(li); });
            if (!track.children.length) return;
            sec.hidden = false;
            const rail = sec.querySelector('[data-ce-crecent-rail]');
            if (rail) rail.setAttribute('data-ce-rail', '');
            if (window.BO && window.BO.init) window.BO.init(sec);
            const clear = sec.querySelector('[data-ce-crecent-clear]');
            if (clear) clear.addEventListener('click', () => {
              try { localStorage.removeItem(RKEY); } catch (e) { /* fine */ }
              sec.hidden = true;
              if (window.BO && window.BO.toast) window.BO.toast('Recently viewed cleared');
            });
          });
      };
      // after the page has loaded (idle): nothing here is needed before the grid
      const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 600));
      if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
    });
  };

  const all = new Map();
  const init = (root = document) => {
    descs(root);
    recent(root);
    const found = root.matches && root.matches('[data-ce-coll]') ? [root] : [...root.querySelectorAll('[data-ce-coll]')];
    found.forEach((el) => { if (!all.has(el)) all.set(el, new Coll(el)); });
  };

  window.addEventListener('popstate', () => {
    all.forEach((inst, el) => { if (el.isConnected) inst.render(window.location.href, { push: false }); });
  });
  stickMq.addEventListener('change', () => all.forEach((inst) => inst.stickCheck()));
  // leaving the drawer width with the drawer open (a tablet turned, a window widened): it becomes the sidebar
  drawerMq.addEventListener('change', () => { if (!drawerMq.matches) all.forEach((inst) => inst.close()); });
  let rz = 0;
  window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(() => descs(document), 200); });

  window.CEColl = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    all.forEach((inst, el) => { if (e.target.contains(el)) { inst.destroy(); all.delete(el); } });
  });
})();
